import React from 'react';
import { View, TouchableOpacity, StyleSheet, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { Club } from '../../lib/api/clubs';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

interface ClubCardProps {
  club: Club;
  onPress: () => void;
  status?: 'joined' | 'pending' | 'not-member' | 'approved';
  showJoinButton?: boolean;
  onJoinPress?: () => void;
  onChatPress?: () => void;
}

const ClubCard: React.FC<ClubCardProps> = ({ 
  club, 
  onPress, 
  status, 
  showJoinButton = false,
  onJoinPress,
  onChatPress
}) => {
  const { theme } = useTheme();
  const handleJoinPress = () => {
    onJoinPress?.();
  };

  return (
    <TouchableOpacity style={styles.card} onPress={onPress} activeOpacity={0.7}>
      {/* Cover Photo with Gradient Overlay */}
      <View style={styles.coverContainer}>
        {club.coverPhotoUrl ? (
          <Image 
            source={{ uri: club.coverPhotoUrl }} 
            style={styles.coverPhoto}
            resizeMode="cover"
          />
        ) : (
          <LinearGradient
            colors={[theme.primary + '40', theme.accent + '40']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.coverPhoto}
          >
            <Ionicons name="people" size={48} color={theme.primary} />
          </LinearGradient>
        )}
        <LinearGradient
          colors={['transparent', 'rgba(0,0,0,0.7)']}
          style={styles.coverGradient}
        >
          <ThemedText style={styles.name} numberOfLines={2}>{club.name}</ThemedText>
          <View style={styles.coverMeta}>
            <View style={styles.coverMetaItem}>
              <Ionicons name="people-outline" size={14} color="#FFFFFF" />
              <ThemedText style={styles.coverMetaText}>{club.memberCount}</ThemedText>
            </View>
            <View style={styles.coverMetaItem}>
              <Ionicons name="location-outline" size={14} color="#FFFFFF" />
              <ThemedText style={styles.coverMetaText}>{club.city}</ThemedText>
            </View>
          </View>
        </LinearGradient>
        
        {/* Paid / Free badge — top left */}
        <View style={[styles.membershipBadge, club.isPaid ? styles.membershipBadgePaid : styles.membershipBadgeFree]}>
          <Ionicons
            name={club.isPaid ? 'card-outline' : 'lock-open-outline'}
            size={11}
            color="#FFFFFF"
          />
          <ThemedText style={styles.membershipBadgeText}>
            {club.isPaid ? `₹${(club.annualFee ?? 0).toLocaleString('en-IN')}/yr` : 'Free'}
          </ThemedText>
        </View>

        {/* Status Badge on Cover */}
        {status && status !== 'not-member' && (
          <View style={[
            styles.statusBadge,
            status === 'joined' && styles.statusBadgeJoined,
            status === 'pending' && styles.statusBadgePending,
          ]}>
            <ThemedText style={[
              styles.statusText,
              status === 'joined' && styles.statusTextJoined,
              status === 'pending' && styles.statusTextPending,
            ]}>
              {status === 'joined' ? '✓ Joined' : '⏱ Pending'}
            </ThemedText>
          </View>
        )}
        
        {/* Chat Button for Joined Members */}
        {status === 'joined' && onChatPress && (
          <TouchableOpacity 
            style={styles.chatButton}
            onPress={() => {
              onChatPress();
            }}
            activeOpacity={0.7}
          >
            <Ionicons name="chatbubbles" size={20} color="#FFFFFF" />
          </TouchableOpacity>
        )}
      </View>

      <View style={[styles.content, { backgroundColor: theme.card }]}>
        <View style={styles.tagContainer}>
          <View style={[styles.tag, { backgroundColor: theme.primary + '20' }]}>
            <Ionicons name="pricetag" size={12} color={theme.primary} />
            <ThemedText style={[styles.tagText, { color: theme.primary }]}>{club.category}</ThemedText>
          </View>
        </View>

        <ThemedText style={[styles.description, { color: theme.textSecondary }]} numberOfLines={2}>
          {club.description}
        </ThemedText>

        {showJoinButton && status === 'not-member' && (
          <TouchableOpacity 
            style={[styles.joinButton, { backgroundColor: theme.primary }]}
            onPress={handleJoinPress}
            activeOpacity={0.8}
          >
            <Ionicons name="add-circle-outline" size={18} color="#000000" />
            <ThemedText style={styles.joinButtonText}>Join Club</ThemedText>
          </TouchableOpacity>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    marginHorizontal: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    overflow: 'hidden',
  },
  coverContainer: {
    height: 160,
    position: 'relative',
  },
  coverPhoto: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  coverGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 16,
    paddingTop: 40,
  },
  name: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 6,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  coverMeta: {
    flexDirection: 'row',
    gap: 16,
  },
  coverMetaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  coverMetaText: {
    fontSize: 13,
    color: '#FFFFFF',
    fontWeight: '500',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  statusBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  statusBadgeJoined: {
    backgroundColor: 'rgba(34, 197, 94, 0.95)',
  },
  statusBadgePending: {
    backgroundColor: 'rgba(245, 158, 11, 0.95)',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  statusTextJoined: {},
  statusTextPending: {},
  chatButton: {
    position: 'absolute',
    bottom: 12,
    right: 12,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(196, 150, 62, 0.95)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  content: {
    padding: 16,
  },
  tagContainer: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
  },
  tagText: {
    fontSize: 12,
    fontWeight: '600',
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  joinButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderRadius: 12,
  },
  joinButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#000000',
  },
  membershipBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 10,
  },
  membershipBadgeFree: {
    backgroundColor: 'rgba(34, 197, 94, 0.90)',
  },
  membershipBadgePaid: {
    backgroundColor: 'rgba(196, 150, 62, 0.95)',
  },
  membershipBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#FFFFFF',
  },
});

export default ClubCard;
